import { tableStyles } from 'css/tableStyles';
import React, { useMemo, useState } from 'react';
import DataTable from 'components/DataTable';
import {
  DATE_FORMATS,
  canItemBeSyncedInSalesChannel,
  formatDate,
  getPageLimit,
  showError,
  showSuccess,
  updatePageLimit,
} from 'utils';
import { Loading } from 'components/loading/Loading';
import DeleteActionForTable from 'components/DeleteActionForTableData';
import styles from './index.module.scss';
import Currency from 'components/Currency/Currency';
import InventoryItemId from 'components/InventoryItemId';
import {
  removeInventoryItemFromSalesChannel, restoreInventoryItemToSalesChannel, updateMenuItemInSalesChannel,
} from 'services/api/salesChannel';
import _ from 'lodash';
import ActionButton from 'components/ActionButton';
import InventoryItemsFilters from './inventoryItemsFilters';
import EditActionForTable from 'components/EditActionForTableData';
import CustomLink from 'components/TableLink';

export const SyncItemButton = ({ item, salesChannelId, onSuccess, btnClass='' }) => {
  const [loading, setLoading] = useState(false);
  const canBeSynced = canItemBeSyncedInSalesChannel(item);
  const title = canBeSynced ? 'Sync Now' : 'Sync Pending';
          
  const updateMenuItemInSalesChannelFn = async(params) => {
    try {
      setLoading(true);
      await updateMenuItemInSalesChannel(params);
      await onSuccess();
      showSuccess('Item synced');
    } catch(e) {
      showError('Error while syncing item');
    } finally {
      setLoading(false);
    }
  };

  if(!canBeSynced) return null;
  return(
    <ActionButton
      text={title}
      active={!loading && canBeSynced}
      isLoading={loading}
      onClick={() => {
        updateMenuItemInSalesChannelFn({
          salesChannelId,
          itemId: item?.inventoryItem?.objectId,
        });
      }}
      customClass={btnClass}
    />
  );
};

const InventoryItems = (props) => {
  const { apiFilters, salesChannelId, inventoryItems = {}, fetchSalesChannelInventoryItemsFn, loading, filters, setApiFilters } = props;
  const [restoreMappingLoading, setRestoreMappingLoading] = useState<boolean>(false);

  const restoreInventoryItem = async (id: string) => {
    try {
      setRestoreMappingLoading(true);
      await restoreInventoryItemToSalesChannel(id);
      showSuccess('Item restored successfully.');
      await fetchSalesChannelInventoryItemsFn();
    } catch (e) {
      showError(e);
    } finally {
      setRestoreMappingLoading(false);
    }
  };

  const deleteInventoryItem = async (inventoryItemId: string) => {
    try {
      await removeInventoryItemFromSalesChannel({
        salesChannelId,
        inventoryItemId,
      });
      showSuccess('Item removed successfully.');
      await fetchSalesChannelInventoryItemsFn();
    } catch (e) {
      showError(e);
    }
  };

  const columns = useMemo(
    () => [
      {
        name: 'ItemID',
        sortable: false,
        width: '180px',
        // eslint-disable-next-line react/display-name
        cell: (row) =>
          <InventoryItemId itemDetails={_.get(row, 'inventoryItem', row)} />,
      },
      {
        name: 'MappingId',
        sortable: false,
        width: '180px',
        // eslint-disable-next-line react/display-name
        cell: (row) =>
          <CustomLink text={row?.objectId} linkTo={`/sales-channel/inventory-item/${row.objectId}`} />,
      },
      {
        name: 'Name',
        sortable: true,
        width: '350px',
        cell: (row) => (
          <div className="text-wrap">{_.get(row, 'inventoryItemName', _.get(row, 'inventoryItem.name', '-'))}</div>
        ),
      },
      {
        name: 'Price',
        sortable: true,
        // eslint-disable-next-line react/display-name
        cell: (row) => (
          <Currency
            amount={parseFloat(row?.inventoryItem?.price?.toFixed(2) || row?.price?.toFixed(2))}
            default={'-'}
          />
        ),
      },
      {
        name: 'Quantity',
        sortable: false,
        cell: (row) => <div>{row?.inventoryItem?.stock || row?.stock || '-'}</div>,
      },
      {
        name: 'CategoryId',
        sortable: false,
        cell: (row) => <div>
          {_.get(row, 'inventoryItem.category.objectId', row?.category?.objectId)}
        </div>,
      },
      {
        name: 'CategoryName',
        sortable: false,
        cell: (row) => <div>
          {_.get(row, 'inventoryItemSubCategoryName', _.get(row, 'inventoryItem.category.description', '-'))}
        </div>,
      },
      {
        name: 'GroupId',
        sortable: false,
        cell: (row) => <div>
          {_.get(row, 'inventoryItem.category.group.objectId', row?.category?.group?.objectId)}
        </div>,
      },
      {
        name: 'GroupName',
        sortable: false,
        cell: (row) => <div>
          {_.get(row, 'inventoryItemCategoryName', _.get(row, 'inventoryItem.category.group.name', '-'))}
        </div>,
      },
      {
        name: 'Deleted',
        sortable: false,
        width: '200px',
        cell: (row) => {
          return (
            <div>
              {row?.isDeleted ? 'true' : 'false'}
            </div>
          );
        },
      },
      {
        name: 'Last Sync Needed',
        selector: 'lastSyncNeeded',
        sortable: false,
        width: '200px',
        cell: row => <div>
          {row.lastSyncNeeded?.iso ? formatDate(row.lastSyncNeeded?.iso, DATE_FORMATS.MM_DD_YYYY_hh_mm_A) : '-'}
        </div>,
      },
      {
        name: 'Last Sync Performed',
        selector: 'lastSyncPerformed',
        sortable: false,
        width: '200px',
        cell: row => <div>
          {row.lastSyncPerformed?.iso ? formatDate(row.lastSyncPerformed?.iso, DATE_FORMATS.MM_DD_YYYY_hh_mm_A) : '-'}
        </div>,
      },
      {
        name: 'Sync Status',
        sortable: false,
        width: '200px',
        cell: (row) => {
          const canBeSynced = canItemBeSyncedInSalesChannel(row);
          return (
            <div className={canBeSynced ? styles['pending-sync'] : styles['synced']}>
              {canBeSynced ? 'Pending Sync' : 'Synced'}
            </div>
          );
        },
      },
      {
        name: 'Action',
        selector: '',
        sortable: true,
        width: '300px',
        // eslint-disable-next-line react/display-name
        cell: (row: Record<string, any>) => {
          return (
            <div className={'d-flex align-items-center'}>
              <SyncItemButton btnClass='mr-2' item={row} salesChannelId={salesChannelId} onSuccess={fetchSalesChannelInventoryItemsFn} />
              <div className='mr-2 ml-0'>
                <EditActionForTable onClick={()=>{
                  props.history.push(`/sales-channel/inventory-item/${row.objectId}`);
                }} />
              </div>
              {row?.isDeleted ? 
                <ActionButton
                  text={'Restore'}
                  active={!restoreMappingLoading}
                  isLoading={restoreMappingLoading}
                  onClick={() => restoreInventoryItem(row?.objectId)}
                  customClass='mr-2 ml-0'
                /> : 
                <div className='mx-3'>
                  <DeleteActionForTable
                    onClick={() => deleteInventoryItem(row?.inventoryItem?.objectId || row?.objectId)}
                  />
                </div>
              }
            </div>
          );
        },
      },
    ],
    [apiFilters, restoreMappingLoading],
  );

  const onChangePage = (page: number) => {
    filters.page = page - 1;
    fetchSalesChannelInventoryItemsFn();
  };

  const onChangeRowsPerPage = (rowsPerPage: number, currentPage: number) => {
    filters.limit = rowsPerPage;
    updatePageLimit(rowsPerPage);
    onChangePage(currentPage);
  };

  return (
    <div className={styles['inventory-items-container']}>
      <div className={styles['section-header']}>
        <div className={styles['section-heading']}>Inventory Items</div>
      </div>
      <InventoryItemsFilters 
        onSubmit={setApiFilters} 
        {...props}
      />
      {loading && <Loading type={'mini'} />}
      <DataTable
        columns={columns}
        noHeader={true}
        data={inventoryItems?.items}
        highlightOnHover={true}
        defaultSortField="createdAt"
        customStyles={tableStyles}
        keyField="objectId"
        pagination={true}
        paginationServer={true}
        paginationPerPage={getPageLimit()}
        paginationDefaultPage={(filters.page || 0) + 1}
        paginationTotalRows={inventoryItems.total}
        onChangePage={onChangePage}
        onChangeRowsPerPage={onChangeRowsPerPage}
      />
    </div>
  );
};

export default InventoryItems;
